import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import ConfigureSLD from "../../../pages/wide-area-network-twin/configure-sld";
import { RootContext } from "../../../contexts/root-context";
import { WNT_BASE_API_URL, WNT_ERROR_LIST } from "../../../strings";
import BoundingBoxDrawer from "./bounding-box-drawer";
import { Button } from "@abb/abb-common-ux-react";
import singleLineDiagramConfig from './single-line-diagram.json';
import "./single-line-diagram.css";
import { Link } from 'react-router-dom';
import { getToken } from '../../Authentication/get-token';

const SingleLineDiagram = () => {
  const { showConfigureSLDPage, setShowConfigureSLDPage } = useContext(RootContext);
  const [sldBlobUrl, setSLDBlobUrl] = useState(null);
  const [sldNotAvailable, setSLDNotAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [liveData, setLiveData] = useState({});
  const [boundingBoxes, setBoundingBoxes] = useState([]);
  const intervalRef = useRef(null);
  const blobUrlRef = useRef(null);

  const getAuthHeaders = () => {
    const tokenObj = getToken();
    return {
        "Content-Type": "application/json",
        ...(tokenObj?.headers || {}),
    };
  };
  
  const boxes = useMemo(() => (
    boundingBoxes
      .filter((box) => box.text.includes("Bus"))
      .map((box) => ({
        label: box.text,
        polygon: box.polygon.map(p => [p.X, p.Y])
      }))
  ), [boundingBoxes]);

  const fetchLiveBusMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${WNT_BASE_API_URL}/live-bus-metrics`, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        console.warn("Failed to fetch live bus metrics");
        setError(true);
        return;
      }
      const result = await response.json();
      setLiveData(result?.data || {});
      setError(false);
    } catch (error) {
      setError(true);
    }
  }, []);

  const fetchSLD = useCallback(async () => {
    setLoading(true);
    setError(false);
    setSLDNotAvailable(false);

    const fileName = localStorage.getItem("selectedSLDFile");
    console.log(fileName ? `Fetching specific file: ${fileName}` : "Fetching latest available file...");
    try {
      const requestBody = {
        projectName: "wnt",
        fileType: "sld",
      };

      // Add fileName to the body ONLY if it exists
      if (fileName) {
        requestBody.fileName = fileName;
      }

      const response = await fetch(`${WNT_BASE_API_URL}/sld-fetch`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.response = response;
        throw error;
      }

      const apiResponse = await response.json();

      if (!apiResponse.success || !apiResponse.data?.imageBase64) {
        console.log("API response indicates failure or no image data found.");
        setSLDNotAvailable(true);
        setLoading(false);
        setError(true);
        return;
      }

      const { data } = apiResponse;

      localStorage.setItem("selectedSLDFile", data.fileName);

      const fileExtension = fileName?.split(".").pop()?.toLowerCase();
      let imageType = "image/jpeg";
      if (fileExtension === "png") {
        imageType = "image/png";
      } else if (fileExtension === "gif") {
        imageType = "image/gif";
      }

      const byteCharacters = atob(data.imageBase64);
      const byteArray = new Uint8Array([...byteCharacters].map((c) => c.charCodeAt(0)));
      const blob = new Blob([byteArray], { type: imageType });

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      
      const newBlobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = newBlobUrl;
      setSLDBlobUrl(newBlobUrl);
      setBoundingBoxes(data.boundingBoxes || []);
      setSLDNotAvailable(false);
      setLoading(false);

      await fetchLiveBusMetrics();
      intervalRef.current = setInterval(fetchLiveBusMetrics, 15 * 60 * 1000);
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        if(status === 404) {
          setSLDNotAvailable(true);
          setLoading(false);
        }
        else if (status >= 400 && status < 500) {
          setSLDNotAvailable(true);
          setLoading(false);
          setError(true);
        } else if (status >= 500 && status < 600) {
          setError(true);
        } else {
          setError(true);
        }
      } else {
        setError(true);
        setSLDNotAvailable(true);
        setLoading(false);
      }
    }
  }, [fetchLiveBusMetrics]);
  
  useEffect(() => {
    fetchSLD();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [fetchSLD]);

  const handleConfigureClick = () => {
    setShowConfigureSLDPage(true);
  };

  const renderSLDContent = () => {
    if (error) {
      return (
        <span style={{ margin: 'auto' }} color="dark-grey">
          {WNT_ERROR_LIST.ERROR_FETCH_DATA}
          <br />
          <Link style={{ color: '#1f1f1f' }} onClick={fetchSLD}>
            Retry
          </Link>
        </span>
      );
    }

    if (loading) 
      return <div className="loading-msg">Loading SLD...</div>;

    if (sldBlobUrl) {
      return (
        <BoundingBoxDrawer
          imageSrc={sldBlobUrl}
          boxes={boxes}
          liveData={liveData}
        />
      );
    } else if (sldNotAvailable) {
      return <div className="no-sld">No SLD available.</div>;
    } else {
      return <div className="loading-msg">Checking for available SLD...</div>;
    }
  };

  if (showConfigureSLDPage) {
    return <ConfigureSLD />;
  }
  
  return (
    <div className="single-line-diagram-container">
      <div className="wnt-tab-title">
        <h2 className="wnt-filter-header-opt" style={{ marginTop: 0, marginBottom: 0 }}>
          {singleLineDiagramConfig?.GENERAL?.enable && (singleLineDiagramConfig?.GENERAL?.tabTitle)}
        </h2>
        <div>
        {singleLineDiagramConfig?.configureSLDButton?.enable && (
          <Button
            style={{ margin: "10px", width: "160px" }}
            {...singleLineDiagramConfig.configureSLDButton.properties}
            onClick={handleConfigureClick}
          />
        )}
        {singleLineDiagramConfig?.refreshButton?.enable && (
          <Button
            style={{ margin: "10px", width: "160px" }}
            {...singleLineDiagramConfig.refreshButton.properties}
            onClick={() => fetchLiveBusMetrics()}
          />
        )}
        </div>
      </div>
      {singleLineDiagramConfig?.sld?.enable && (
        <div className="single-line-diagram">{renderSLDContent()}</div>
      )}
    </div>
  );
};

export default SingleLineDiagram;